import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { FormGroup, FormControl, Validators, AbstractControl } from "@angular/forms";
import { DictionaryService } from "src/app/share/service/password-services/dictionary.service";
import { UserListingInterface } from "src/app/share/interface/user.interface";
import { NgxSpinnerService } from "ngx-spinner";
import { UserDataService } from "src/app/share/data-service/user.data.service";
import { AlertService } from "src/app/share/service/alert.service";
import { PasswordPolicyDto } from 'src/app/share/interface/password.interface';
import { PasswordPolicyService } from "src/app/share/service/password-services/password-policy.service";
import { PasswordCheckService } from "src/app/share/service/password-services/password-check.service";

@Component({
  selector: 'app-password-expiry-dialog',
  templateUrl: './password-expiry-dialog.component.html',
  styleUrls: ['./password-expiry-dialog.component.scss'],
})

export class PasswordExpiryDialogComponent {

    form = new FormGroup({
        newPassword: new FormControl('', [
            Validators.required,
            this.validatePassword.bind(this)
        ]),
		confirmPassword: new FormControl('', [
			Validators.required,
			() => this.customConfirmValidator()
		])
	}, { validators: this.passwordMatchValidator });

	selectedUserId: UserListingInterface;

	// Hide Password button booleans
    hideNew = true;
    hideConfirm = true;

	// Password Policy Requirements
	// If active, has the requirement value stored
	// If inactive, is set to null
	minimumChars: number | null = null;
	maximumChars: number | null = null;
	uppercaseReq: number | null = null;
	lowercaseReq: number | null = null;
	numberReq: number | null = null;
	specialChars: string | null = null;
	disallowedSpecialChars: string | null = null;
	commonStrings: string[] = [];
	allowDictionaryWords: boolean = true; // Default to true since we negate dictionaryCheck
	sequenceNum: number | null = null;
	prevPasswordsChecked: number | null = null

	// Holds the password validation booleans
	hasValidMinLength = false;
	hasValidMaxLength = false;
	hasUpperCase = false;
	hasLowerCase = false;
	hasNumber = false;
	hasSpecialChar = false;
	hasInvalidSpecialChar = false;
	containsCommonStrings = false;
	containsDictionaryWord = false;
	consecutiveChars = false;

	// Possible future customisable configurations
	specialCharReq: any = 1;
	maxDictionaryWordLength: number = 3;

	//Edit
	matches: string = '';

	// Password visibility
	hidePassword = true;
	hideCurrentPassword = true;
	hideConfirmPassword = true;

	// Regular Expressions
	private specialCharsRegex: RegExp;
	private disallowedCharsRegex: RegExp;

	constructor(
		private ngxSpinnerService: NgxSpinnerService,
		private userDataService: UserDataService,
		private alertService: AlertService,
		private passwordPolicyService: PasswordPolicyService,
		private passwordCheckService: PasswordCheckService,

		public dialogRef: MatDialogRef<PasswordExpiryDialogComponent>,

		@Inject(DictionaryService) private dictionaryService: DictionaryService,
		@Inject(MAT_DIALOG_DATA) public data: {
			user: UserListingInterface
		}
	) {}

	async ngOnInit(): Promise<void> {

		try {

			// Initialize password policies from backend
			await this.initializePasswordPolicies();

			this.selectedUserId = this.data.user;

			// Regular Expression initialization
			// Converts string policies into regular expressions
			// Only if the respective policies are active
			if (this.specialChars) {
				this.specialCharsRegex = new RegExp(`[${this.escapeRegex(this.specialChars)}]`, 'g');
			}
			if (this.disallowedSpecialChars) {
				this.disallowedCharsRegex = new RegExp(`[${this.escapeRegex(this.disallowedSpecialChars)}]`, 'g');
			}
		}
		catch (error) {
			console.error('Initialization failed:', error);
			this.dialogRef.close();
		}
	}

	// Cleanup of any pending operations
	ngOnDestroy() {
		this.ngxSpinnerService.hide();
	}

	// Initializes the variables for each policy with their values
	private initializePasswordPolicies(): void {
		this.passwordPolicyService.getActivePolicy().subscribe({
			next: (policy: PasswordPolicyDto) => {
				// Update component properties with policy values
				this.minimumChars = policy.minimumCharacters;
				this.maximumChars = policy.maximumCharacters;
				this.uppercaseReq = policy.uppercaseCount;
				this.lowercaseReq = policy.lowercaseCount;
				this.numberReq = policy.digitsCount;
				this.specialChars = policy.specialCharacters || "";
				this.disallowedSpecialChars = policy.disallowedCharacters || "";

				// Convert disallowed sequences string to array if needed
				this.commonStrings = policy.disallowedSequences
					? policy.disallowedSequences.split(',')
					: [];

				this.allowDictionaryWords = !(policy.dictionaryCheck ?? false);
				this.sequenceNum = policy.disallowedRepeatNumber;
				this.prevPasswordsChecked = policy.numberOfPreviousPswd;

				// Initialize regex patterns after getting policy values
				if (this.specialChars) {
					this.specialCharsRegex = new RegExp(`[${this.escapeRegex(this.specialChars)}]`, 'g');
				}
				if (this.disallowedSpecialChars) {
					this.disallowedCharsRegex = new RegExp(`[${this.escapeRegex(this.disallowedSpecialChars)}]`, 'g');
				}
			},
			error: (err) => {
				console.error('Failed to load password policies', err);
				// Optionally set default values or show error message
			}
		});
	}

	// Escapes characters within a string that require it to prevent errors
	private escapeRegex(str: string): string {
		return str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
	}

	// Resets all validation flags
	private resetValidationFlags(): void {
		this.hasValidMinLength = this.minimumChars === null;
		this.hasValidMaxLength = this.maximumChars === null;
		this.hasUpperCase = this.uppercaseReq === null;
		this.hasLowerCase = this.lowercaseReq === null;
		this.hasNumber = this.numberReq === null;
		this.hasSpecialChar = !this.specialChars;
		this.hasInvalidSpecialChar = false;
		this.containsCommonStrings = false;
		this.containsDictionaryWord = false;
		this.consecutiveChars = false;
	}

	// Checks if the new password is within policy requirements
	validatePassword(control: AbstractControl) {
		const value = control.value;

		// Reset all validation flags
		this.resetValidationFlags();

		if (!value) return { required: true };


		// Minimum length requirement check
		if (this.minimumChars !== null) {
			this.hasValidMinLength = value.length >= this.minimumChars
		}
		else {
			this.hasValidMinLength = true;
		}

		// Maximum length requirement check
		if (this.maximumChars !== null) {
			this.hasValidMaxLength = value.length <= this.maximumChars;
		}
		else {
			this.hasValidMaxLength = true;
		}

		// Uppercase requirement check
		if (this.uppercaseReq !== null) {
			this.matches = value.match(/[A-Z]/g) || [];
			this.hasUpperCase = this.matches.length >= this.uppercaseReq;
		}
		else {
			this.hasUpperCase = true;
		}

		// Lowercase requirement check
		if (this.lowercaseReq !== null) {
			this.matches = value.match(/[a-z]/g) || [];
			this.hasLowerCase = this.matches.length >= this.lowercaseReq;
		}
		else {
			this.hasLowerCase = true;
		}

		// Number requirement check
		if (this.numberReq !== null) {
			this.matches = value.match(/\d/g) || [];
			this.hasNumber = this.matches.length >= this.numberReq;
		}
		else {
			this.hasNumber = true;
		}

		// Special character requirement check
		if (this.specialChars) {
			this.matches = value.match(this.specialCharsRegex) || [];
			this.hasSpecialChar = this.matches.length >= this.specialCharReq;
		}
		else {
			this.hasSpecialChar = true;
		}

		// Invalid character check
		if (this.disallowedSpecialChars) {
			this.matches = value.match(this.disallowedCharsRegex) || [];
			this.hasInvalidSpecialChar = this.matches.length >= 1;
		}

		// Common string combination check
		// Only checks if the commonStrings list is not null nor empty
		if (this.commonStrings && this.commonStrings.length > 0) {
			this.commonStrings.forEach((combination: string) => {
				if (value.includes(combination)) {
					this.containsCommonStrings = true;
				}
			});
		}

		// Checks for dictionary words within the password 
		if (!this.allowDictionaryWords && this.maxDictionaryWordLength !== null) {
			this.containsDictionaryWord = this.dictionaryService.containsDictionaryWord(
				value,
				this.maxDictionaryWordLength
			);
		}

		// Checks for consecutive character sequence
		// Check only runs if the length of input is greater than or equal to the policy
		if (this.sequenceNum !== null && value.length >= this.sequenceNum) {
			for (let i = 0; i <= value.length - this.sequenceNum; i++) {
				let allSame = true;
				for (let j = 1; j < this.sequenceNum; j++) {
					if (value[i] !== value[i + j]) {
						allSame = false;
						break;
					}
				}
				if (allSame) {
					this.consecutiveChars = true;
					break;
				}
			}
		}

		if (!this.hasValidMinLength || !this.hasValidMaxLength ||
			!this.hasUpperCase || !this.hasLowerCase || !this.hasNumber || !this.hasSpecialChar ||
			this.hasInvalidSpecialChar || this.containsCommonStrings ||
			this.consecutiveChars || this.containsDictionaryWord) {
			return { invalidPassword: true };
		}

		return null;
	}

	// Checks if the Confirm Password entry matches the New Password Entry
	passwordMatchValidator(group: FormGroup) {
		const newPass = group.get('newPassword')?.value;
		const confirmPass = group.get('confirmPassword')?.value;
		return newPass === confirmPass ? null : { mismatch: true };
	}

	// Validator for Confirm Password Entry
	customConfirmValidator() {
		const confirmControl = this.form?.get('confirmPassword');
		const newPassControl = this.form?.get('newPassword');

		if (!confirmControl || !newPassControl) return null;

		// Only validates mismatch if both fields have values
		if (confirmControl.value && newPassControl.value) {
			return confirmControl.value === newPassControl.value ? null : { mismatch: true };
		}

		return null;
	}

	// Actively checks if the Confirm Password Entry matches the New Password Entry
	checkPasswords() {
		const confirmControl = this.form.get('confirmPassword');
		if (confirmControl.value) {
			confirmControl.updateValueAndValidity();
			this.form.updateValueAndValidity();
		}
	}	

	// Confirm Password Error control
	showConfirmPasswordError(): boolean {
		const confirmControl = this.form.get('confirmPassword');
		return confirmControl.invalid && (confirmControl.dirty || confirmControl.touched);
	}

	// Password Errors
	getConfirmPasswordError(): string {
		const confirmControl = this.form.get('confirmPassword');

		if (confirmControl.hasError('required')) {
			return 'Please Confirm New Password';
		}

		if (this.form.hasError('mismatch')) {
			return 'Confirm Password Does Not Match New Password';
		}

		return '';
	}

	// Updates the user's password with the new one entered
	async submit() {
		if (this.form.valid) {
			this.ngxSpinnerService.show();

			// Checks for Null user id
			if (!this.data?.user?.id) {
				console.error('User ID is missing');
				this.ngxSpinnerService.hide();
				this.alertService.openSnackBar(
					'User identification failed. Please login again.',
					'DISMISS',
					5000
				);
				this.dialogRef.close({ success: false });
				return;
			}

			const userId = this.selectedUserId.id;

			try {
				// Checks the new password for user PII
				const passesPiiCheck = await this.passwordCheckService.checkPasswordForPii(userId, this.form.value.newPassword);
				if (!passesPiiCheck) {
					this.ngxSpinnerService.hide();
					this.alertService.openSnackBar(
						'Password must not contain your name, email, or ID.'
					);
					return;
				}

				// Check against previous passwords if required
				if (this.prevPasswordsChecked && this.prevPasswordsChecked > 0) {
					const isValid = await this.passwordCheckService.checkPreviousPasswords(userId, this.form.value.newPassword);

					if (!isValid) {
						this.ngxSpinnerService.hide();
						this.alertService.openSnackBar(
							'Password cannot match your previous ' + this.prevPasswordsChecked + ' passwords! Please change and submit again'
						);
						return;
					}
				}

				// Save new password
				this.userDataService.updatePassword(userId, this.form.value.newPassword).subscribe({
					next: async (x) => {
						try {
							// Then save to password history (PasswordUpdates)
							await this.passwordCheckService.saveNewPassword(
								userId,
								this.form.value.newPassword
							);

							this.ngxSpinnerService.hide();

							// Close and login
							this.dialogRef.close({ success: true });
						}
						// Error catch for PasswordUpdates saving
						catch (historyError) {
							this.ngxSpinnerService.hide();
							this.alertService.openSnackBar('Password was updated but history could not be saved');
						}
					},
					// Error catch for failure to update password
					error: (error) => {
						this.ngxSpinnerService.hide();
						this.alertService.openSnackBar('Something Went Wrong. Please Try Again Later');
					}
				});
			}
			// Error catch for failure to update password
			catch (error) {
				this.ngxSpinnerService.hide();
				this.alertService.openSnackBar('Something Went Wrong. Please Try Again Later');
			}
		}
	}

	// Function of close button
	closeDialog() {
		this.dialogRef.close({ success: false });
	}
}